import { useMemo } from 'react'
import GcpCollapsible from '@/components/gcp/GcpCollapsible'
import GcpQueryGroupsTable from '@/components/gcp/GcpQueryGroupsTable'
import GcpRuleBasedCallout from '@/components/gcp/GcpRuleBasedCallout'
import GcpSectionErrorBanner from '@/components/gcp/GcpSectionErrorBanner'
import {
  asNumber,
  asRecordArray,
  asString,
  formatUsd,
  getSectionData,
  getSlotUtilizationSummary,
  lookupSkuExplanation,
  type RuleBasedRecommendation,
} from '@/components/gcp/gcpHelpers'
import type { GcpAnalysisResult, GcpRecurringQueryGroup } from '@/services/gcpCosts'
import { isGcpSectionError } from '@/services/gcpCosts'

interface GcpBigQuerySectionProps {
  analysis: GcpAnalysisResult
}

function DataTable({
  columns,
  rows,
}: {
  columns: Array<{ key: string; label: string; align?: 'left' | 'right' }>
  rows: Record<string, unknown>[]
}) {
  if (rows.length === 0) {
    return <p className="text-sm text-gray-500">No data.</p>
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200 text-sm">
        <thead className="bg-gray-50">
          <tr>
            {columns.map((col) => (
              <th
                key={col.key}
                className={`px-3 py-2 font-medium text-gray-600 ${col.align === 'right' ? 'text-right' : 'text-left'}`}
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((row, index) => (
            <tr key={index}>
              {columns.map((col) => (
                <td
                  key={col.key}
                  className={`px-3 py-2 text-gray-800 ${col.align === 'right' ? 'text-right' : 'text-left'}`}
                >
                  {formatCell(row[col.key], col.key)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function formatCell(value: unknown, key: string): string {
  if (value == null) return '—'
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return '—'
    if (
      key.includes('cost') ||
      key.includes('usd') ||
      key.endsWith('_amount') ||
      key.includes('monthly')
    ) {
      return formatUsd(value)
    }
    return value.toLocaleString('en-US', { maximumFractionDigits: 4 })
  }
  if (typeof value === 'string') return value
  return JSON.stringify(value)
}

export default function GcpBigQuerySection({ analysis }: GcpBigQuerySectionProps) {
  const section = analysis.sections.bigquery

  const bqSection = getSectionData(analysis, 'bigquery')

  const recurringGroups = useMemo((): GcpRecurringQueryGroup[] => {
    if (!bqSection || !Array.isArray(bqSection.recurring_query_groups)) return []
    return bqSection.recurring_query_groups as GcpRecurringQueryGroup[]
  }, [bqSection])

  const unpartitionedTables = useMemo(() => {
    return asRecordArray(bqSection?.unpartitioned_tables).map((row) => {
      const dataset = asString(row.dataset_id)
      const table = asString(row.table_name)
      return dataset && table ? `${dataset}.${table}` : table || dataset
    })
  }, [bqSection])

  const recommendations = useMemo(
    () => (asRecordArray(bqSection?.recommendations) as RuleBasedRecommendation[]) ?? [],
    [bqSection]
  )

  if (isGcpSectionError(section)) {
    return (
      <GcpSectionErrorBanner
        title="BigQuery analysis failed"
        error={section.error}
        missingRoleHint={section.missing_role_hint}
      />
    )
  }

  if (!bqSection) {
    return null
  }

  const slotSummary = getSlotUtilizationSummary(
    bqSection.slot_utilization as Record<string, unknown> | null
  )

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-500">
        Recurring queries and storage — the specific workloads driving BigQuery spend.
      </p>

      {recommendations.map((rec, index) => (
        <GcpRuleBasedCallout key={`${rec.type ?? 'rec'}-${index}`} recommendation={rec} />
      ))}

      <GcpCollapsible title="Recurring query groups" defaultOpen>
        <GcpQueryGroupsTable groups={recurringGroups} unpartitionedTables={unpartitionedTables} />
      </GcpCollapsible>

      <GcpCollapsible title="Top individual queries" defaultOpen={false}>
        <DataTable
          columns={[
            { key: 'estimated_cost_usd', label: 'Est. cost', align: 'right' },
            { key: 'bytes', label: 'Bytes', align: 'right' },
            { key: 'user_email', label: 'User' },
            { key: 'query', label: 'Query' },
          ]}
          rows={asRecordArray(bqSection.expensive_queries).map((row) => {
            const tb = asNumber(row.tb_processed)
            const bytesLabel =
              tb != null
                ? tb >= 1
                  ? `${tb.toFixed(3)} TB`
                  : `${(tb * 1024).toFixed(1)} GB`
                : '—'
            return {
              estimated_cost_usd: asNumber(row.estimated_cost_usd),
              bytes: bytesLabel,
              user_email: asString(row.user_email),
              query: (asString(row.query_preview) || asString(row.query)).slice(0, 120),
            }
          })}
        />
      </GcpCollapsible>

      <GcpCollapsible title="Spend by SKU" defaultOpen={false}>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left font-medium text-gray-600">Month</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600">SKU</th>
                <th className="px-3 py-2 text-right font-medium text-gray-600">Net cost</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {asRecordArray(bqSection.costs).map((row, index) => {
                const sku = asString(row.sku)
                const explanation = lookupSkuExplanation(sku)
                return (
                  <tr key={`${sku}-${index}`}>
                    <td className="px-3 py-2 text-gray-800">{asString(row.month)}</td>
                    <td className="px-3 py-2 text-gray-800" title={explanation ?? sku}>
                      {sku}
                    </td>
                    <td className="px-3 py-2 text-right text-gray-800">
                      {formatUsd(asNumber(row.net_cost))}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </GcpCollapsible>

      <GcpCollapsible title="Query patterns by user" defaultOpen={false}>
        <DataTable
          columns={[
            { key: 'user_email', label: 'User / service account' },
            { key: 'query_count', label: 'Queries', align: 'right' },
            { key: 'total_cost_usd', label: 'Total cost', align: 'right' },
            { key: 'avg_gb_per_query', label: 'Avg GB/query', align: 'right' },
          ]}
          rows={asRecordArray(bqSection.query_patterns)}
        />
      </GcpCollapsible>

      <GcpCollapsible title="Storage by dataset" defaultOpen={false}>
        <DataTable
          columns={[
            { key: 'dataset_id', label: 'Dataset' },
            { key: 'total_gb', label: 'Total GB', align: 'right' },
            { key: 'active_gb', label: 'Active GB', align: 'right' },
            { key: 'long_term_gb', label: 'Long-term GB', align: 'right' },
            { key: 'table_count', label: 'Tables', align: 'right' },
            { key: 'estimated_monthly_cost', label: 'Est. $/mo', align: 'right' },
          ]}
          rows={asRecordArray(bqSection.storage)}
        />
      </GcpCollapsible>

      <GcpCollapsible title="Large unpartitioned tables" defaultOpen={false}>
        <DataTable
          columns={[
            { key: 'dataset_id', label: 'Dataset' },
            { key: 'table_name', label: 'Table' },
            { key: 'size_gb', label: 'Size GB', align: 'right' },
          ]}
          rows={asRecordArray(bqSection.unpartitioned_tables)}
        />
        <p className="mt-2 text-xs text-gray-500">
          Unpartitioned tables force queries to scan the full table — partitioning by date can
          dramatically cut query cost.
        </p>
      </GcpCollapsible>

      <GcpCollapsible title="Stale tables" defaultOpen={false}>
        <DataTable
          columns={[
            { key: 'dataset_id', label: 'Dataset' },
            { key: 'table_name', label: 'Table' },
            { key: 'size_gb', label: 'Size GB', align: 'right' },
            { key: 'days_since_created', label: 'Days old', align: 'right' },
          ]}
          rows={asRecordArray(bqSection.stale_tables)}
        />
      </GcpCollapsible>

      {slotSummary ? (
        <GcpCollapsible title="Slot utilization & pricing" defaultOpen={false}>
          <p className="text-sm text-gray-800">{slotSummary}</p>
        </GcpCollapsible>
      ) : null}
    </div>
  )
}
